import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import {FragmentMapper} from 'app/fragment/core/fragment-mapper';
import {FragmentIndexService} from 'app/fragment/indexing/fragment-index.service';
import {ClauseFragment, ClauseType, FragmentType} from 'app/fragment/types';
import {ClauseGroupFragment} from 'app/fragment/types/clause-group-fragment';
import {UUID} from 'app/utils/uuid';
import {environment} from 'environments/environment';
import {catchError, map} from 'rxjs/operators';
import {BaseService} from './base.service';

@Injectable({
  providedIn: 'root',
})
export class ScheduleTableService extends BaseService {
  private readonly _scheduleTableGenerateFragmentsUrl: string = `${environment.apiHost}/schedule-table/generate-fragments`;

  constructor(
    private _fragmentIndexService: FragmentIndexService,
    protected _snackbar: MatSnackBar,
    private _http: HttpClient
  ) {
    super(_snackbar);
  }

  public getScheduleTableFragments(clauseGroup: ClauseGroupFragment, validAt: number): Promise<ClauseGroupFragment> {
    const params: {[param: string]: string} = {
      fragmentId: clauseGroup.id.value,
    };

    if (validAt) {
      params['validAt'] = validAt.toString();
    }

    return this._http
      .get<ClauseGroupFragment>(this._scheduleTableGenerateFragmentsUrl, {params})
      .pipe(
        map((response: any) => FragmentMapper.deserialise(response) as ClauseGroupFragment),
        catchError((error: any) => {
          this._handleError(
            error,
            'Failed to generate schedule table. Please retry or contact support',
            'schedule-table-error'
          );
          return Promise.reject(error);
        })
      )
      .toPromise();
  }

  /**
   * Calculates the index that should be shown for the generated schedule table, and sets it within the fragment index
   * service. This can always use the index of the first SI within the group, and simply remove a trailing 'a' if
   * necessary, note that as the first SI is used this only needs to check for 'a' and not any generic letter, and we
   * do not need to have separate logic to handle NDRs as this will already be reflected in the SI index.
   */
  public calculateScheduleTableIndex(authoredClauseGroup: ClauseGroupFragment, generatedClauseId: UUID): void {
    const firstSiId: UUID = authoredClauseGroup.children.find(
      (f) => f.is(FragmentType.CLAUSE) && (f as ClauseFragment).clauseType === ClauseType.SPECIFIER_INSTRUCTION
    )?.id;
    if (!firstSiId) {
      return;
    }

    const firstSiIndex: string = this._fragmentIndexService.getIndex(firstSiId);
    const generatedClauseIndex: string = firstSiIndex.endsWith('a')
      ? firstSiIndex.substring(0, firstSiIndex.length - 1)
      : firstSiIndex;
    this._fragmentIndexService.setCustomIndex(generatedClauseId, generatedClauseIndex);
  }
}
